import { openAddContactModal } from "@/components/AddContactModal/AddContactModal";
import { EditContactForm } from "@/components/EditContactForm/EditContactForm";
import { Icons } from "@/components/Icons/Icons";
import { Panel } from "@/components/Panel/Panel";
import { TagsRenderer } from "@/components/TagInput/TagsRenderer";
import { contactModel, ContactRecord } from "@/models/contact";
import { getDefaultContactDirectoryEntity } from "@/models/contactDirectory";
import { getCurrentDealEntity } from "@/models/deal";
import { userIsCreatorConstraint } from "@/orm/model";
import { Entity } from "@/orm/types";
import { Route } from "@/paths";
import {
  ActionIcon,
  Button,
  Group,
  Stack,
  Table,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { useAtomValue } from "jotai";
import { CgComment } from "react-icons/cg";
import { generatePath, Link } from "react-router-dom";

export const Contacts = () => {
  const dealEntity = getCurrentDealEntity();

  const { entities: contactEntities, loading } = useAtomValue(
    dealEntity.getObserveManyBelongingsAtom<ContactRecord>(
      contactModel,
      "belongsToDealIds",
      { constraints: [userIsCreatorConstraint()] },
    ),
  );

  const sortedContactEntities = (contactEntities || []).sort((a, b) =>
    (a?.data?.name || "").localeCompare(b?.data?.name || ""),
  );

  if (loading) return null;

  return (
    <Panel label="Contacts" p={0}>
      {(sortedContactEntities || []).length > 0 && (
        <Table.ScrollContainer minWidth={800}>
          <Table verticalSpacing="sm" horizontalSpacing={6} striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th px="sm">Name / Email</Table.Th>
                <Table.Th>Phone / Website</Table.Th>
                <Table.Th>Firm / Location </Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedContactEntities.map((contact) => (
                <Contact key={contact.id} entity={contact} />
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Stack gap={"xs"} p={"xs"}>
        <Button variant="outline" onClick={openAddContactModal}>
          Add Contact
        </Button>
      </Stack>
    </Panel>
  );
};

const Contact = ({ entity }: { entity: Entity<ContactRecord> }) => {
  const { data: contact } = entity;
  const cdRecord = getDefaultContactDirectoryEntity();

  const { data } = useAtomValue(cdRecord.getObserveEntityRecordAtom());
  const jobTagOptions = data?.jobTitleTags || [];

  const remove = async () => {
    entity.removeBelongsToRelation(getCurrentDealEntity(), "belongsToDealIds");
  };

  const openEditModal = (entity: Entity<ContactRecord>) => {
    modals.open({
      title: "Add Contact",
      children: (
        <EditContactForm
          callToAction="Update Contact"
          initialValues={entity.data!}
          onSubmit={async (newValues) => {
            await entity.update(newValues as ContactRecord);
            modals.closeAll();
          }}
        />
      ),
    });
  };

  if (!contact) return null;

  return (
    <>
      <Table.Tr>
        <Table.Td px="sm">
          <Group gap={"xs"}>
            <Stack gap={2}>
              <Group gap={3}>
                {contact.name}
                {contact.remarks && (
                  <Tooltip label={contact.remarks} inline>
                    <ActionIcon size={"xs"} variant="transparent">
                      <CgComment />
                    </ActionIcon>
                  </Tooltip>
                )}{" "}
              </Group>
              {contact.email}
            </Stack>
          </Group>
        </Table.Td>
        <Table.Td>
          {contact.phone}
          <br />
          {contact.website}
        </Table.Td>
        <Table.Td>
          {contact.firm}
          <br />
          {contact.location}
        </Table.Td>
        <Table.Td maw={100}>
          <Group gap={4}>
            <TagsRenderer
              tagIds={contact.jobTitleIds}
              tagOptions={jobTagOptions}
            />
          </Group>
        </Table.Td>

        <Table.Td w={140}>
          <Group gap={"xs"}>
            <Tooltip label="View contact">
              <ActionIcon
                variant="light"
                size={"lg"}
                component={Link}
                to={generatePath(Route.Contact, { contactId: entity.id })}
              >
                <Icons.View />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Edit contact">
              <ActionIcon
                variant="light"
                size={"lg"}
                onClick={() => openEditModal(entity)}
              >
                <Icons.Edit />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Remove contact from deal">
              <ActionIcon
                variant="light"
                color="red"
                size={"lg"}
                onClick={() => remove()}
              >
                <Icons.Remove />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    </>
  );
};
